import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { monedaInputNumberProps } from "../../config/moneda";
import { useStoreListasPrecios } from "./useStoreListasPrecios";
import "./listasPrecios.css";
import { PERMISOS, puedeAcceder } from "../../servicios/sesionSeguridad";
import BotonInstructivo from "../../ComponentesGlobales/BotonInstructivo";

const normalizar = (valor) => String(valor || "").trim().toLowerCase();

const ListasPrecios = () => {
  const puedeModificar = puedeAcceder(PERMISOS.PRECIOS_MODIFICAR);
  const store = useStoreListasPrecios();
  const inicializar = useStoreListasPrecios((state) => state.inicializar);
  const [seccion, setSeccion] = useState("productos");
  const [busqueda, setBusqueda] = useState("");
  const [dialogoCopia, setDialogoCopia] = useState(false);
  const [descripcionCopia, setDescripcionCopia] = useState("");

  useEffect(() => {
    inicializar();
  }, [inicializar]);

  const registros = seccion === "productos" ? store.mercaderias : store.clientes;
  const filtrados = useMemo(() => {
    const texto = normalizar(busqueda);
    if (!texto) return registros;
    return registros.filter((item) =>
      normalizar(`${item.sCodigo} ${item.sDescripcion} ${item.sDocumento}`).includes(texto),
    );
  }, [registros, busqueda]);

  const abrirCopia = () => {
    setDescripcionCopia(`${store.resumen.sDescripcion} - Copia`);
    setDialogoCopia(true);
  };

  const confirmarCopia = async () => {
    if (await store.copiarLista({
      descripcion: descripcionCopia,
    })) setDialogoCopia(false);
  };

  const abrirNuevaLista = () => {
    setSeccion("productos");
    setBusqueda("");
    store.nuevaLista();
  };

  const seleccionarLista = (id) => {
    setSeccion("productos");
    setBusqueda("");
    store.seleccionarLista(id);
  };

  return (
    <main className="lp-page">
      <header className="lp-header">
        <div className="lp-header-main">
          <span className="lp-header-icon" aria-hidden="true">
            <i className="pi pi-tags" aria-hidden="true" />
          </span>
          <div>
            <h1>Listas de precios</h1>
            <p>Definí vigencias, productos, precios y clientes para cada lista.</p>
          </div>
        </div>
        <div className="lp-header-actions">
          <BotonInstructivo to="/tutorial/listas-precios" sobreColor />
          <Button
            label="Nueva lista"
            icon="pi pi-plus"
            className="lp-action-primary"
            onClick={abrirNuevaLista}
            disabled={!puedeModificar || store.guardando}
          />
          <Button
            label="Duplicar"
            icon="pi pi-copy"
            severity="secondary"
            outlined
            className="lp-action-secondary"
            onClick={abrirCopia}
            disabled={!puedeModificar || !store.resumen.Id || store.guardando}
          />
        </div>
      </header>

      <div className="lp-layout vista-entrada-lateral">
        <aside className="lp-listas">
          <div className="lp-panel-title">
            <span className="lp-section-icon" aria-hidden="true"><i className="pi pi-list" /></span>
            <div>
              <small>Administración</small>
              <strong>Listas disponibles</strong>
            </div>
            <span className="lp-count" aria-label={`${store.listas.length} listas`}>{store.listas.length}</span>
          </div>
          <div className="lp-listas-scroll">
            {store.listas.length === 0 && !store.cargando && (
              <div className="lp-listas-empty">
                <i className="pi pi-inbox" aria-hidden="true" />
                <span>No hay listas creadas</span>
              </div>
            )}
            {store.listas.map((lista) => (
              <button
                type="button"
                key={lista.Id}
                className={store.editorActivo && store.resumen.Id === lista.Id ? "activo" : ""}
                onClick={() => seleccionarLista(lista.Id)}
              >
                <span>
                  <strong>{lista.sDescripcion}</strong>
                  <small>{lista.Mercaderias} productos · {lista.Clientes} clientes</small>
                </span>
                <i className={`pi ${lista.bVigente ? "pi-check-circle" : "pi-clock"}`} />
              </button>
            ))}
          </div>
        </aside>

        <section className="lp-editor">
          {store.cargando ? (
            <div className="lp-state"><i className="pi pi-spin pi-spinner" /><p>Cargando lista…</p></div>
          ) : !store.editorActivo ? (
            <div className="lp-state lp-state-inicial">
              <i className="pi pi-tags" />
              <h2>Gestioná tus listas de precios</h2>
              <p>
                {store.listas.length
                  ? "Seleccioná una lista para consultar o modificar sus productos."
                  : "Todavía no hay listas creadas. Creá la primera para asignar productos y precios."}
              </p>
              <Button
                label="Nueva lista"
                icon="pi pi-plus"
                onClick={abrirNuevaLista}
                disabled={!puedeModificar}
              />
            </div>
          ) : (
            <>
              <div className="lp-editor-title">
                <span className="lp-section-icon" aria-hidden="true"><i className="pi pi-file-edit" /></span>
                <div>
                  <small>{store.resumen.Id ? `Lista #${store.resumen.Id}` : "Nueva lista"}</small>
                  <strong>Datos generales</strong>
                </div>
                <span className={`lp-editor-status ${store.resumen.bActivo ? "activo" : "inactivo"}`}>
                  {store.resumen.bActivo ? "Activa" : "Inactiva"}
                </span>
              </div>
              <div className="lp-form">
                <label className="lp-wide">
                  <span>Descripción</span>
                  <input
                    value={store.resumen.sDescripcion}
                    onChange={(e) => store.setResumen("sDescripcion", e.target.value)}
                    placeholder="Ej. Mayoristas agosto"
                  />
                </label>
                <label>
                  <span>Vigente desde</span>
                  <input
                    type="date"
                    value={store.resumen.dFechaDesde || ""}
                    onChange={(e) => store.setResumen("dFechaDesde", e.target.value)}
                  />
                </label>
                <label>
                  <span>Vigente hasta</span>
                  <input
                    type="date"
                    value={store.resumen.dFechaHasta || ""}
                    onChange={(e) => store.setResumen("dFechaHasta", e.target.value)}
                  />
                </label>
                <label className="lp-check">
                  <input
                    type="checkbox"
                    checked={Boolean(store.resumen.bActivo)}
                    onChange={(e) => store.setResumen("bActivo", e.target.checked)}
                  />
                  <span>Lista activa</span>
                </label>
                <label className="lp-check">
                  <input
                    type="checkbox"
                    checked={Boolean(store.resumen.bListaPorDefecto)}
                    onChange={(e) => store.setResumen("bListaPorDefecto", e.target.checked)}
                  />
                  <span>Lista por defecto</span>
                </label>
              </div>

              <div className="lp-tabs">
                <button
                  type="button"
                  className={seccion === "productos" ? "activo" : ""}
                  onClick={() => setSeccion("productos")}
                >
                  Productos <span>{store.mercaderias.filter((x) => x.bSeleccionado).length}</span>
                </button>
                <button
                  type="button"
                  className={seccion === "clientes" ? "activo" : ""}
                  onClick={() => setSeccion("clientes")}
                >
                  Clientes <span>{store.clientes.filter((x) => x.bSeleccionado).length}</span>
                </button>
                <label className="lp-search">
                  <i className="pi pi-search" />
                  <span className="lp-sr-only">Buscar en la lista</span>
                  <input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder={`Buscar ${seccion}…`}
                  />
                </label>
              </div>

              <div className="lp-table-wrap">
                {seccion === "productos" ? (
                  <table className="lp-table">
                    <thead><tr><th>Usar</th><th>Producto</th><th>Moneda</th><th>Precio sin IVA</th></tr></thead>
                    <tbody>
                      {filtrados.map((item) => (
                        <tr key={item.Id} className={item.bSeleccionado ? "" : "inactivo"}>
                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(item.bSeleccionado)}
                              disabled={!puedeModificar}
                              onChange={(e) => store.setMercaderia(item.Id, { bSeleccionado: e.target.checked })}
                            />
                          </td>
                          <td><strong>{item.sDescripcion}</strong><small>{item.sCodigo}</small></td>
                          <td>
                            <select
                              value={Number(item.MonedaTipo_Id) || store.monedaTipos[0]?.Id || ""}
                              disabled={!puedeModificar || !item.bSeleccionado}
                              onChange={(e) => store.setMercaderia(item.Id, { MonedaTipo_Id: Number(e.target.value) })}
                            >
                              {store.monedaTipos.map((moneda) => (
                                <option key={moneda.Id} value={moneda.Id}>
                                  {moneda.sDescripcion}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <InputNumber
                              className="lp-price-input"
                              inputClassName="lp-price-control"
                              value={Number(item.nPrecio) || 0}
                              onValueChange={(e) => store.setMercaderia(item.Id, { nPrecio: e.value ?? 0 })}
                              disabled={!puedeModificar || !item.bSeleccionado}
                              min={0}
                              {...monedaInputNumberProps}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="lp-table">
                    <thead><tr><th>Asignar</th><th>Cliente</th><th>Documento</th><th>Localidad</th></tr></thead>
                    <tbody>
                      {filtrados.map((item) => (
                        <tr key={item.Id} className={item.bSeleccionado ? "" : "inactivo"}>
                          <td>
                            <input
                              type="checkbox"
                              checked={Boolean(item.bSeleccionado)}
                              disabled={!puedeModificar}
                              onChange={(e) => store.setCliente(item.Id, e.target.checked)}
                            />
                          </td>
                          <td><strong>{item.sDescripcion}</strong><small>{item.sCodigo}</small></td>
                          <td>{item.sDocumento || "—"}</td>
                          <td>{item.Localidad || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {filtrados.length === 0 && <div className="lp-empty">No hay resultados para mostrar.</div>}
              </div>

              <footer className="lp-footer">
                <span>
                  {store.resumen.Id
                    ? `Lista #${store.resumen.Id}`
                    : "Los productos y clientes seleccionados se guardarán al crear la lista."}
                </span>
                <div>
                  <Button
                    label="Guardar configuración"
                    icon="pi pi-save"
                    className="lp-save"
                    onClick={store.guardarLista}
                    loading={store.guardando}
                    disabled={!puedeModificar}
                  />
                </div>
              </footer>
            </>
          )}
        </section>
      </div>

      <Dialog
        header="Duplicar lista"
        className="lp-copy-dialog"
        visible={dialogoCopia}
        onHide={() => setDialogoCopia(false)}
        style={{ width: "min(520px, 94vw)" }}
        modal
        draggable={false}
      >
        <div className="lp-copy">
          <label>
            <span>Descripción de la nueva lista</span>
            <input
              value={descripcionCopia}
              onChange={(e) => setDescripcionCopia(e.target.value)}
              placeholder="Ej. Mayoristas - Copia"
              autoFocus
            />
          </label>
          <p className="lp-copy-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            Se copiarán la configuración y los productos. Los clientes conservarán sus listas actuales; cuando un cliente no tenga una lista asignada, se aplicará la lista de precios predeterminada.
          </p>
          <Button
            label="Crear copia"
            icon="pi pi-copy"
            className="lp-save lp-copy-submit"
            onClick={confirmarCopia}
            loading={store.guardando}
            disabled={!puedeModificar || !descripcionCopia.trim()}
          />
        </div>
      </Dialog>
    </main>
  );
};

export default ListasPrecios;
